import { matchTemplateRoomToProperty } from "@/data/checklistTemplates";
import { supabase } from "@/integrations/supabase/client";
import type { ListingScanDetectedItem } from "@/lib/listingPhotoScan";
import { Room, Task, DEFAULT_CLEANING_SETTINGS, DEFAULT_MAINTENANCE_SETTINGS } from "@/types/checklist";

export type GenerateChecklistItem = ListingScanDetectedItem & {
  /** Attach listing photo as task reference (cleaning tasks only). */
  useSourceAsReferencePhoto: boolean;
  requireAfterPhoto: boolean;
};

/** Merge extra rooms/tasks into a base Room[] (adds to matching rooms, appends new ones). */
export function mergeChecklistRooms(base: Room[], extra: Room[]): Room[] {
  const map = new Map<string, Task[]>();
  for (const r of base) map.set(r.name, [...r.tasks]);
  for (const r of extra) {
    const existing = map.get(r.name);
    if (existing) existing.push(...r.tasks);
    else map.set(r.name, [...r.tasks]);
  }
  return [...map.entries()].map(([name, tasks]) => ({ name, tasks }));
}

export function buildCleaningTaskRoomsFromScan(
  items: GenerateChecklistItem[],
  propertyRoomNames: string[]
): {
  rooms: Room[];
  refLinks: { roomName: string; taskText: string; photoUrl: string }[];
} {
  const byRoom: Record<string, Task[]> = {};
  const refLinks: { roomName: string; taskText: string; photoUrl: string }[] = [];

  for (const item of items) {
    if (item.destination !== "cleaning_task") continue;
    const roomName = matchTemplateRoomToProperty(item.roomName.trim(), propertyRoomNames);
    const taskText = (item.suggestedTask || `Clean ${item.name.toLowerCase()}`).trim();
    if (!taskText) continue;

    const task: Task = {
      text: taskText,
      task_type: "cleaning",
      require_reference_photo: item.useSourceAsReferencePhoto && !!item.sourcePhotoUrl,
      require_before_photo: false,
      require_after_photo: item.requireAfterPhoto,
      cleaning_settings: {
        ...DEFAULT_CLEANING_SETTINGS,
        require_reference_photo: item.useSourceAsReferencePhoto && !!item.sourcePhotoUrl,
        require_before_photo: false,
        require_after_photo: item.requireAfterPhoto,
      },
      maintenance_settings: { ...DEFAULT_MAINTENANCE_SETTINGS },
    };

    if (!byRoom[roomName]) byRoom[roomName] = [];
    byRoom[roomName].push(task);

    if (item.useSourceAsReferencePhoto && item.sourcePhotoUrl.trim()) {
      refLinks.push({ roomName, taskText, photoUrl: item.sourcePhotoUrl.trim() });
    }
  }

  const rooms = Object.entries(byRoom).map(([name, tasks]) => ({ name, tasks }));
  return { rooms, refLinks };
}

export async function insertTaskReferencePhotoFromUrl(args: {
  propertyId: string;
  checklistId: string;
  roomName: string;
  taskText: string;
  photoUrl: string;
  displayOrder?: number;
}): Promise<void> {
  const { error } = await supabase.from("task_reference_photos").insert({
    property_id: args.propertyId,
    room_name: args.roomName.trim(),
    task_text: args.taskText.trim(),
    photo_url: args.photoUrl.trim(),
    display_order: args.displayOrder ?? 0,
    checklist_id: args.checklistId,
  });
  if (error) throw error;
}

export function unitCheckItemsFromScan(items: GenerateChecklistItem[]): GenerateChecklistItem[] {
  return items.filter(
    (i) => i.destination === "unit_check" || i.destination === "maintenance"
  );
}

export function cleaningItemsFromScan(items: GenerateChecklistItem[]): GenerateChecklistItem[] {
  return items.filter((i) => i.destination === "cleaning_task");
}
